// Models
import {
  CaseCategory,
  Case,
  LiveDrop,
  Stats,
  Announcement,
  User,
  PublicUser,
  Openings,
  SteamInventoryItem,
  RiskFactor,
  DepositTrade,
  DepositHistoryProfile,
  UserReferral,
  DepositStreakStatusResponse,
  UserStats,
} from './models'
import type { PromotionalEventResponse, UserWPStats } from '@/types/promotionalEvents'

// Response for /init
export interface InitResponse {
  arena: any
  caseCategories: CaseCategory[]
  cases: Case[]
  vipCases?: Case[]
  drops: LiveDrop[]
  lucky: LiveDrop[]
  recentArenaWinners: any
  safebox: any
  stats: Stats
  announcement: Announcement
  currentEvent?: PromotionalEventResponse // Added for promotional events
}

// Response for profile/me
export interface ProfileResponse {
  user: User
  openings: Openings
  stats?: UserStats
  myStats?: UserWPStats
  depositStreak?: DepositStreakStatusResponse
  redepositNextAvailableAt?: string | number | null
}

export type ProfileDepositStatus =
  | 'pending'
  | 'in_hold'
  | 'success'
  | 'fail'
  | 'hold_approved'
  | 'hold_returned'

export interface ProfileDepositsParams {
  page?: number
  pageSize?: number
  status?: ProfileDepositStatus
}

export interface ProfileDepositsResponse {
  pagination: {
    page: number
    pageSize: number
    total?: number
  }
  result: DepositHistoryProfile[]
}

// Response for /profile/public/{userId}
export interface PublicProfileResponse {
  user: PublicUser
  openings: Openings
  stats?: UserStats
}

// Response for /inventory/steam-inventory
export interface SteamInventoryResponse {
  items: SteamInventoryItem[]
  riskFactor: RiskFactor
}

// Response for /inventory/deposit
export interface DepositResponse {
  deposit: DepositTrade
  success: boolean
}

// Response for /profile/me/referral-code
export type ReferralStatsResponse = UserReferral | null

export interface useGiftCodeResponse {
  success: boolean
  value: number
  message: string
  error?: string
}

export interface ReDepositResponse {
  success: boolean
  message?: string
  error?: string
  creditedAmount?: number
  feeAmount?: number
  wpCredit?: number
  giveawayEntries?: number
  nextAvailableAt?: string
}

export interface GiveawayItem {
  id?: string
  name: string
  image: string
  price: number
  weapon?: string
  skin?: string
  exterior?: string
  rarity?: string
  color?: string
  stattrak?: boolean
}

export interface GiveawayTypeOwner {
  id: string
  name: string
  image?: string | null
}

export interface GiveawayFairness {
  mode: 'legacy' | 'provably_fair'
  algorithm: string
  serverSeedHash: string | null
  serverSeed: string | null
  participantSnapshotHash: string | null
  totalTickets: number | null
  winningTicket: number | null
  winnerTicketStart: number | null
  winnerTicketEnd: number | null
  revealedAt: string | null
}

export interface GiveawayTypeRequirements {
  name: string
  frequencyInterval?: number
  requiredSteamLevel: number
  requiredLevel: number
  requiredDepositAmount: number
  requiredDepositSinceDays: number
  emailVerifiedRequired: boolean
  tokenCost?: number
  maxParticipationsSinceDeposit?: number
  winCooldownMinutes?: number
  ownerUserId?: string
  requiredReferralCodeId?: string | null
  requiredReferralCode?: string | null
  minimumTickets?: number
  maxTickets?: number
  isRecurring?: boolean
  owner?: GiveawayTypeOwner | null
}

export interface GiveawayParticipant {
  id: string
  name: string
  avatar?: string | null
  joinedAt: string
  tickets?: number
}

export interface GiveawayRoundResponse {
  id: string
  status: GiveawayRoundResponse.status
  item: GiveawayItem
  participantCount: number
  minimumParticipants: number
  minimumTickets?: number
  endsAt?: string | null
  userJoined: boolean
  canJoin: boolean
  userTickets?: number
  totalTickets?: number
  userParticipationsCount?: number
  userCooldownUntil?: string | null
  fairness?: GiveawayFairness | null
  giveawayType: GiveawayTypeRequirements
  participants?: GiveawayParticipant[]
  winner?: GiveawayWinner | null
}

export namespace GiveawayRoundResponse {
  export enum status {
    _0 = 0,
    _10 = 10,
    _20 = 20,
    _30 = 30,
    _40 = 40,
  }
}

export interface GiveawayWinner {
  id: string
  name: string
  avatar?: string | null
}
